/**
 * iter143 — useDraftAutosave hook.
 *
 * Persists an object to localStorage whenever it changes (debounced
 * 500 ms) and offers a restore-on-mount check. Used by the Bookings
 * proposal modal so a browser refresh, accidental Back, or dropped
 * network never loses edits.
 *
 * Storage key: `kbrj:draft:<scope>:<id>` — one draft per booking id
 * (or "new" for the create flow). Drafts older than 7 days are ignored
 * on mount so stale entries never resurface.
 */
import { useCallback, useEffect, useRef } from "react";

const PREFIX = "kbrj:draft:";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function key(scope, id) {
  return `${PREFIX}${scope}:${id || "new"}`;
}

/** Read a saved draft. Returns `{ value, savedAt }` or null. */
export function loadDraft(scope, id) {
  try {
    const raw = window.localStorage.getItem(key(scope, id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.savedAt && Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(key(scope, id));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(scope, id) {
  try {
    window.localStorage.removeItem(key(scope, id));
  } catch { /* noop */ }
}

/**
 * useDraftAutosave — writes `value` to localStorage on change,
 * debounced by `debounceMs`. `enabled=false` short-circuits everything
 * (used when the modal is closed).
 *
 * Returns `{ savedAt, savingRef }` — savedAt is a Date (or null) of the
 * last successful save.
 */
export function useDraftAutosave({
  scope,
  id,
  value,
  enabled = true,
  debounceMs = 500,
  onDirty,          // called on every scheduled save (used to flip "unsaved" flag)
}) {
  const timer = useRef(null);
  const lastSavedRef = useRef(null);
  const dirtyCbRef = useRef(onDirty);
  useEffect(() => { dirtyCbRef.current = onDirty; }, [onDirty]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        const payload = { value, savedAt: Date.now() };
        window.localStorage.setItem(key(scope, id), JSON.stringify(payload));
        lastSavedRef.current = payload.savedAt;
        dirtyCbRef.current?.(payload.savedAt);
      } catch { /* quota exceeded etc. — silent */ }
    }, debounceMs);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [scope, id, value, enabled, debounceMs]);

  return { lastSavedRef };
}

/**
 * useBeforeUnloadWarn — attaches a native `beforeunload` warning while
 * `dirty=true`. The browser's stock "leave site?" dialog is shown.
 * `enabled=false` removes the handler.
 */
export function useBeforeUnloadWarn(dirty, enabled = true) {
  useEffect(() => {
    if (!enabled || !dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, enabled]);
}

/** Format a `Date` or timestamp for the "Draft saved · 2s ago" chip. */
export function fmtDraftAgo(savedAt) {
  if (!savedAt) return "";
  const secs = Math.floor((Date.now() - savedAt) / 1000);
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

export { useDraftAutosave as default };
